/**
 * Access Control Page
 * @description 서비스 접근 권한 및 역할 관리 페이지
 */

import { useState, useEffect } from 'react';
import './tenant-admin.css';
import {
  Badge, Button, Card, Checkbox, Dialog, DialogContent, DialogDescription,
  DialogHeader, DialogTitle, DialogTrigger, Input, Label, Select, SelectContent,
  SelectItem, SelectTrigger, SelectValue, Tabs, TabsContent, TabsList, TabsTrigger,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@shared/ui/primitives';
import { useAuthStore } from '@/stores/authStore';
import { dataService } from '@/services/dataService';
import {
    Shield,
    Users,
    Settings,
    Plus,
    Edit,
    Trash2,
    Key,
    Lock,
    Unlock,
    UserCheck,
    Building
} from 'lucide-react';

interface Service {
    id: string;
    name: string;
    description: string;
    endpoint: string;
    icon: string;
    sub_menus?: Array<{
        id: string;
        name: string;
        description: string;
        endpoint: string;
        icon: string;
    }>;
}

interface Role {
    roleId: string;
    roleName: string;
    description: string;
    services: string[];
    members: string[];
}

interface UserOverride {
    userId: string;
    email: string;
    name: string;
    description: string;
    additional_services: string[];
}

interface PermissionMatrix {
    [serviceId: string]: {
        [action: string]: string[];
    };
}

// 새 구조용 인터페이스
interface PermissionTarget {
    groups: string[];
    roles: string[];
    users: string[];
}

interface ServicePermissions {
    name?: string;
    view?: PermissionTarget;
    create?: PermissionTarget;
    update?: PermissionTarget;
    delete?: PermissionTarget;
    [action: string]: PermissionTarget | string | undefined;
}

interface NewAccessControlData {
    roles: {
        schemaVersion?: string;
        roles: Array<{ roleId: string; name: string; description: string }>;
        groups?: Array<{ groupId: string; name: string; description: string }>;
        roleAssignments: Record<string, string[]>;
        teamRoleAssignments?: Record<string, string[]>;
    };
    permissions: Record<string, ServicePermissions>;
    services: string[];
}

interface OrgTeam {
    teamId: string;
    teamName: string;
    status: string;
}

interface OrgUser {
    userId: string;
    displayName: string;
    email?: string;
    teamIds?: string[];
    status?: string;
}

interface AccessControlData {
    services: Service[];
    roles: Role[];
    userOverrides: UserOverride[];
    permissionMatrix: PermissionMatrix;
}

export const AccessControlPage: React.FC = () => {
    const { user } = useAuthStore();
    const [loading, setLoading] = useState(false);
    const [accessData, setAccessData] = useState<AccessControlData>({
        services: [],
        roles: [],
        userOverrides: [],
        permissionMatrix: {}
    });
    // 새 구조 데이터
    const [rawData, setRawData] = useState<NewAccessControlData | null>(null);
    
    const [roleModalOpen, setRoleModalOpen] = useState(false);
    const [userOverrideModalOpen, setUserOverrideModalOpen] = useState(false);
    const [permissionModalOpen, setPermissionModalOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [editingUserOverride, setEditingUserOverride] = useState<UserOverride | null>(null);
    const [editingPermission, setEditingPermission] = useState<{ serviceId: string; action: string } | null>(null);

    // Form states
    const [roleForm, setRoleForm] = useState<Partial<Role>>({});
    const [userOverrideForm, setUserOverrideForm] = useState<Partial<UserOverride>>({});
    // 역할별 서비스 권한 (CRUD)
    const [rolePermissions, setRolePermissions] = useState<Record<string, string[]>>({});
    // 사용자별 서비스 권한 (CRUD)
    const [userPermissions, setUserPermissions] = useState<Record<string, string[]>>({});
    // 권한 편집용 상태
    const [permissionForm, setPermissionForm] = useState<{ roles: string[]; groups: string[]; users: string[] }>({
        roles: [],
        groups: [],
        users: []
    });
    // 그룹(팀) 관리: 조직 팀 목록, 팀별 역할 할당 다이얼로그
    const [orgTeams, setOrgTeams] = useState<OrgTeam[]>([]);
    const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
    const [groupRoleModalOpen, setGroupRoleModalOpen] = useState(false);
    const [editingTeamForRoles, setEditingTeamForRoles] = useState<OrgTeam | null>(null);
    const [groupRoleForm, setGroupRoleForm] = useState<string[]>([]);

    useEffect(() => {
        const currentUser = user;
        if (currentUser?.userType === 'tenant-user') {
            loadAccessControlData(currentUser.tenantId);
            dataService.getTenantOrganization(currentUser.tenantId).then((org: any) => {
                setOrgTeams(Array.isArray(org?.teams) ? org.teams : []);
                setOrgUsers(Array.isArray(org?.users) ? org.users : []);
            }).catch(() => { setOrgTeams([]); setOrgUsers([]); });
        }
    }, [user]);

    const loadAccessControlData = async (tenantId: string) => {
        try {
            setLoading(true);

            // 실제 API 호출 (새 구조)
            const data = await dataService.getTenantAccessControl(tenantId);

            // 새 구조를 기존 UI 호환 형태로 변환 (임시)
            const rolesData = data.roles || {};
            const permissions = data.permissions || {};
            
            // 역할/그룹별 서비스 개수 계산
            const countServices = (id: string, targetKey: 'roles' | 'groups') => {
                let count = 0;
                for (const [serviceId, sp] of Object.entries(permissions)) {
                    if (serviceId === 'schemaVersion') continue;
                    const servicePerm = sp as any;
                    if (servicePerm.view?.[targetKey]?.includes(id)) count++;
                }
                return count;
            };

            // roles 변환: roles.roles만 사용 (타입 제거, groups 미사용)
            const convertedRoles = (rolesData.roles || []).map((r: any) => ({
                roleId: r.roleId,
                roleName: r.name,
                description: r.description,
                services: Array(countServices(r.roleId, 'roles')).fill(''),
                members: rolesData.roleAssignments?.[r.roleId] || []
            }));

            // permissionMatrix 변환
            const permissionMatrix: PermissionMatrix = {};
            for (const [serviceId, servicePerm] of Object.entries(permissions)) {
                if (serviceId === 'schemaVersion') continue;
                const sp = servicePerm as any;
                permissionMatrix[serviceId] = {};
                for (const [action, perm] of Object.entries(sp)) {
                    if (action === 'name') continue;
                    const p = perm as any;
                    permissionMatrix[serviceId][action] = [
                        ...(p.groups || []),
                        ...(p.roles || []),
                        ...(p.users || [])
                    ];
                }
            }

            // userOverrides 추출: permissions에서 users 필드 수집 (임시)
            const userServicesMap: Record<string, string[]> = {};
            for (const [serviceId, servicePerm] of Object.entries(permissions)) {
                if (serviceId === 'schemaVersion') continue;
                const sp = servicePerm as any;
                const viewUsers = sp.view?.users || [];
                for (const userId of viewUsers) {
                    if (!userServicesMap[userId]) userServicesMap[userId] = [];
                    userServicesMap[userId].push(serviceId);
                }
            }
            const userOverrides = Object.entries(userServicesMap).map(([userId, services]) => ({
                userId,
                email: '',
                name: userId,
                description: '개별 사용자 권한',
                additional_services: services
            }));

            setAccessData({
                services: (data.services || []).map((id: string) => ({ id, name: id, description: '', endpoint: '', icon: '' })),
                roles: convertedRoles,
                userOverrides,
                permissionMatrix
            });
            
            // 새 구조 데이터 저장 (teamRoleAssignments 포함)
            const rolesPayload = data.roles || { schemaVersion: '3.0.0', roles: [], groups: [], roleAssignments: {}, teamRoleAssignments: {} };
            setRawData({
                roles: {
                    ...rolesPayload,
                    teamRoleAssignments: rolesPayload.teamRoleAssignments ?? {}
                },
                permissions: data.permissions || {},
                services: data.services || []
            });
        } catch (error) {
            console.error('접근제어 정보를 불러오는데 실패했습니다:', error);
            setAccessData({
                services: [],
                roles: [],
                userOverrides: [],
                permissionMatrix: {}
            });
        } finally {
            setLoading(false);
        }
    };

    // Role handlers
    const handleRoleCreate = () => {
        setEditingRole(null);
        setRoleForm({ services: [], members: [] });
        setRolePermissions({});
        setRoleModalOpen(true);
    };

    const handleRoleEdit = (role: Role) => {
        setEditingRole(role);
        setRoleForm(role);
        // rawData에서 해당 역할의 권한 추출. 서비스별로 하나라도 있으면 조회·생성·수정·삭제 전체로 표시
        const perms: Record<string, string[]> = {};
        if (rawData?.permissions) {
            const targetKey = 'roles';
            for (const [serviceId, sp] of Object.entries(rawData.permissions)) {
                if (serviceId === 'schemaVersion') continue;
                const servicePerm = sp as any;
                let hasAny = false;
                for (const action of ['view', 'create', 'update', 'delete']) {
                    if (servicePerm[action]?.[targetKey]?.includes(role.roleId)) {
                        hasAny = true;
                        break;
                    }
                }
                if (hasAny) perms[serviceId] = ['view', 'create', 'update', 'delete'];
            }
        }
        setRolePermissions(perms);
        setRoleModalOpen(true);
    };

    const handleRoleDelete = (roleId: string) => {
        if (!confirm(`정말로 역할 "${roleId}"를 삭제하시겠습니까?`)) return;
        if (!rawData) return;

        const currentUser = user;
        if (currentUser?.userType !== 'tenant-user') return;

        // roles에서 제거
        const targetKey = 'roles';
        const idKey = 'roleId';
        
        const updatedRolesData = { ...rawData.roles };
        (updatedRolesData[targetKey] as any[]) = (updatedRolesData[targetKey] as any[]).filter((r: any) => r[idKey] !== roleId);
        if (updatedRolesData.roleAssignments?.[roleId]) {
            delete updatedRolesData.roleAssignments[roleId];
        }

        // permissions에서 제거
        const updatedPermissions = JSON.parse(JSON.stringify(rawData.permissions));
        for (const serviceId of rawData.services) {
            if (!updatedPermissions[serviceId]) continue;
            for (const action of ['view', 'create', 'update', 'delete']) {
                if (!updatedPermissions[serviceId][action]?.[targetKey]) continue;
                updatedPermissions[serviceId][action][targetKey] = updatedPermissions[serviceId][action][targetKey].filter((id: string) => id !== roleId);
            }
        }

        dataService.updateTenantAccessControl(currentUser.tenantId, {
            roles: updatedRolesData,
            permissions: updatedPermissions
        }).then(() => {
            setRawData({ ...rawData, roles: updatedRolesData, permissions: updatedPermissions });
            loadAccessControlData(currentUser.tenantId);
        });
    };

    const handleRoleSubmit = () => {
        if (!roleForm.roleId || !roleForm.roleName) return;
        if (!rawData) return;

        // roles 업데이트 (역할만 사용, 타입 없음)
        const updatedRolesData = { ...rawData.roles };
        const targetKey = 'roles';
        const idKey = 'roleId';
        
        if (editingRole) {
            const arr = (updatedRolesData[targetKey] as any[]) || [];
            const idx = arr.findIndex((r: any) => r[idKey] === editingRole.roleId);
            if (idx >= 0) {
                arr[idx] = { [idKey]: roleForm.roleId, name: roleForm.roleName, description: roleForm.description };
            }
        } else {
            const arr = (updatedRolesData[targetKey] as any[]) || [];
            arr.push({
                [idKey]: roleForm.roleId,
                name: roleForm.roleName,
                description: roleForm.description
            });
            updatedRolesData[targetKey] = arr;
        }

        // permissions 업데이트
        const updatedPermissions = JSON.parse(JSON.stringify(rawData.permissions));
        for (const serviceId of rawData.services) {
            if (!updatedPermissions[serviceId]) {
                updatedPermissions[serviceId] = { name: serviceId };
            }
            for (const action of ['view', 'create', 'update', 'delete']) {
                if (!updatedPermissions[serviceId][action]) {
                    updatedPermissions[serviceId][action] = { groups: [], roles: [], users: [] };
                }
                const arr = updatedPermissions[serviceId][action][targetKey];
                const hasAction = rolePermissions[serviceId]?.includes(action);
                const idx = arr.indexOf(roleForm.roleId);
                if (hasAction && idx < 0) arr.push(roleForm.roleId);
                if (!hasAction && idx >= 0) arr.splice(idx, 1);
            }
        }

        // 타입 제거: groups 미사용으로 저장 시 비움
        updatedRolesData.groups = [];

        // API 호출
        const currentUser = user;
        if (currentUser?.userType !== 'tenant-user') return;
        
        dataService.updateTenantAccessControl(currentUser.tenantId, {
            roles: updatedRolesData,
            permissions: updatedPermissions
        }).then(() => {
            setRawData({ ...rawData, roles: updatedRolesData, permissions: updatedPermissions });
            loadAccessControlData(currentUser.tenantId); // UI 갱신
        });

        setRoleModalOpen(false);
        setRoleForm({});
    };

    // User Override handlers
    const handleUserOverrideCreate = () => {
        setEditingUserOverride(null);
        setUserOverrideForm({ additional_services: [] });
        setUserPermissions({});
        setUserOverrideModalOpen(true);
    };

    const handleUserOverrideEdit = (userOverride: UserOverride) => {
        setEditingUserOverride(userOverride);
        setUserOverrideForm(userOverride);
        // rawData에서 해당 사용자의 권한 추출. 서비스별로 하나라도 있으면 조회·생성·수정·삭제 전체로 표시
        const perms: Record<string, string[]> = {};
        if (rawData?.permissions) {
            for (const [serviceId, sp] of Object.entries(rawData.permissions)) {
                if (serviceId === 'schemaVersion') continue;
                const servicePerm = sp as any;
                let hasAny = false;
                for (const action of ['view', 'create', 'update', 'delete']) {
                    if (servicePerm[action]?.users?.includes(userOverride.userId)) {
                        hasAny = true;
                        break;
                    }
                }
                if (hasAny) perms[serviceId] = ['view', 'create', 'update', 'delete'];
            }
        }
        setUserPermissions(perms);
        setUserOverrideModalOpen(true);
    };

    const handleUserOverrideDelete = (userId: string) => {
        if (!confirm(`정말로 사용자 "${userId}"의 추가 권한을 삭제하시겠습니까?`)) return;
        if (!rawData) return;

        const currentUser = user;
        if (currentUser?.userType !== 'tenant-user') return;

        // permissions에서 users 제거
        const updatedPermissions = JSON.parse(JSON.stringify(rawData.permissions));
        for (const serviceId of rawData.services) {
            if (!updatedPermissions[serviceId]) continue;
            for (const action of ['view', 'create', 'update', 'delete']) {
                if (!updatedPermissions[serviceId][action]?.users) continue;
                updatedPermissions[serviceId][action].users = updatedPermissions[serviceId][action].users.filter((id: string) => id !== userId);
            }
        }

        dataService.updateTenantAccessControl(currentUser.tenantId, {
            roles: rawData.roles,
            permissions: updatedPermissions
        }).then(() => {
            setRawData({ ...rawData, permissions: updatedPermissions });
            loadAccessControlData(currentUser.tenantId);
        });
    };

    const handleUserOverrideSubmit = () => {
        if (!userOverrideForm.userId) return;
        if (!rawData) return;

        // permissions 업데이트 - users 필드에 추가/제거
        const updatedPermissions = JSON.parse(JSON.stringify(rawData.permissions));
        for (const serviceId of rawData.services) {
            if (!updatedPermissions[serviceId]) {
                updatedPermissions[serviceId] = { name: serviceId };
            }
            for (const action of ['view', 'create', 'update', 'delete']) {
                if (!updatedPermissions[serviceId][action]) {
                    updatedPermissions[serviceId][action] = { groups: [], roles: [], users: [] };
                }
                const arr = updatedPermissions[serviceId][action].users;
                const hasAction = userPermissions[serviceId]?.includes(action);
                const idx = arr.indexOf(userOverrideForm.userId);
                if (hasAction && idx < 0) arr.push(userOverrideForm.userId);
                if (!hasAction && idx >= 0) arr.splice(idx, 1);
            }
        }

        const currentUser = user;
        if (currentUser?.userType !== 'tenant-user') return;

        dataService.updateTenantAccessControl(currentUser.tenantId, {
            roles: rawData.roles,
            permissions: updatedPermissions
        }).then(() => {
            setRawData({ ...rawData, permissions: updatedPermissions });
            loadAccessControlData(currentUser.tenantId);
        });

        setUserOverrideModalOpen(false);
        setUserOverrideForm({});
    };

    const getServiceName = (serviceId: string) => {
        const service = accessData.services.find(s => s.id === serviceId);
        return service?.name || serviceId;
    };

    /** 사용자별 소속 팀 이름 목록 (orgUsers.teamIds → orgTeams) */
    const getTeamNamesForUser = (userId: string): string[] => {
        const u = orgUsers.find(x => x.userId === userId);
        const teamIds = Array.isArray(u?.teamIds) ? u.teamIds : [];
        return teamIds.map(tid => orgTeams.find(t => t.teamId === tid)?.teamName || tid);
    };

    /** 사용자별 부여된 역할 이름 목록 (직접 할당 + 팀 역할) */
    const getRoleNamesForUser = (userId: string): string[] => {
        if (!rawData?.roles) return [];
        const u = orgUsers.find(x => x.userId === userId);
        const roleIds = new Set<string>();
        const { roleAssignments = {}, teamRoleAssignments = {}, roles: roleList = [] } = rawData.roles;
        for (const [roleId, userIds] of Object.entries(roleAssignments)) {
            if (Array.isArray(userIds) && userIds.includes(userId)) roleIds.add(roleId);
        }
        const teamIds = Array.isArray(u?.teamIds) ? u.teamIds : [];
        for (const tid of teamIds) {
            const rids = teamRoleAssignments[tid];
            if (Array.isArray(rids)) rids.forEach(rid => roleIds.add(rid));
        }
        return [...roleIds].map(rid => (roleList as { roleId: string; name: string }[]).find(r => r.roleId === rid)?.name || rid);
    };

    /** 테이블 행에서 사용자 추가 권한 다이얼로그 열기 (해당 사용자 선택 상태) */
    const openUserOverrideDialogForUser = (userId: string) => {
        const u = orgUsers.find(x => x.userId === userId);
        setUserOverrideForm({ userId, name: u?.displayName || userId });
        const perms: Record<string, string[]> = {};
        if (rawData?.permissions && userId) {
            for (const [serviceId, sp] of Object.entries(rawData.permissions)) {
                if (serviceId === 'schemaVersion') continue;
                const servicePerm = sp as any;
                let hasAny = false;
                for (const action of ['view', 'create', 'update', 'delete']) {
                    if (servicePerm[action]?.users?.includes(userId)) {
                        hasAny = true;
                        break;
                    }
                }
                if (hasAny) perms[serviceId] = ['view', 'create', 'update', 'delete'];
            }
        }
        setUserPermissions(perms);
        setEditingUserOverride(accessData.userOverrides.find(o => o.userId === userId) || null);
        setUserOverrideModalOpen(true);
    };

    /** 선택한 사용자가 역할/팀으로 이미 접근 가능한 서비스 ID 목록 (추가 권한 다이얼로그용) */
    const getServiceIdsFromRoleForUser = (userId: string): string[] => {
        if (!rawData?.roles || !rawData?.permissions) return [];
        const roleAssignments = rawData.roles.roleAssignments || {};
        const teamRoleAssignments = rawData.roles.teamRoleAssignments || {};
        const user = orgUsers.find(u => u.userId === userId);
        const teamIds = Array.isArray(user?.teamIds) ? user.teamIds : [];
        const roleIds = new Set<string>();
        for (const [roleId, userIds] of Object.entries(roleAssignments)) {
            if (Array.isArray(userIds) && userIds.includes(userId)) roleIds.add(roleId);
        }
        for (const teamId of teamIds) {
            const rids = teamRoleAssignments[teamId];
            if (Array.isArray(rids)) rids.forEach(rid => roleIds.add(rid));
        }
        const serviceIds: string[] = [];
        for (const [serviceId, sp] of Object.entries(rawData.permissions)) {
            if (serviceId === 'schemaVersion') continue;
            const servicePerm = sp as Record<string, { roles?: string[] }>;
            for (const action of ['view', 'create', 'update', 'delete']) {
                const roles = servicePerm[action]?.roles || [];
                if (roles.some((r: string) => roleIds.has(r))) {
                    serviceIds.push(serviceId);
                    break;
                }
            }
        }
        return [...new Set(serviceIds)];
    };


    return (
        <div className="tenant-admin-page space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">접근제어 관리</h1>
                <p className="text-gray-600 mt-1">
                    서비스 접근 권한 및 역할을 관리하고 사용자별 추가 권한을 설정할 수 있습니다.
                </p>
            </div>

            {!user || user.userType !== 'tenant-user' ? (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <p className="text-gray-500">테넌트 사용자로 로그인해주세요.</p>
                    </div>
                </div>
            ) : loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : (

                <Tabs defaultValue="services" className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="services" className="flex items-center gap-2">
                            <Settings className="h-4 w-4" />
                            서비스 권한
                        </TabsTrigger>
                        <TabsTrigger value="roles" className="flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            역할 관리
                        </TabsTrigger>
                        <TabsTrigger value="groups" className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            그룹 관리
                        </TabsTrigger>
                        <TabsTrigger value="user-overrides" className="flex items-center gap-2">
                            <UserCheck className="h-4 w-4" />
                            사용자 추가 권한
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="roles" className="space-y-4">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h2 className="text-lg font-semibold">역할 목록</h2>
                                <p className="text-sm text-gray-500">
                                    {accessData.roles.length}개의 역할
                                </p>
                            </div>
                            <Dialog open={roleModalOpen} onOpenChange={setRoleModalOpen}>
                                <DialogTrigger asChild>
                                    <Button onClick={() => { setEditingRole(null); setRoleForm({ services: [], members: [] }); setRolePermissions({}); setRoleModalOpen(true); }} className="flex items-center gap-2">
                                        <Plus className="h-4 w-4" />
                                        역할 추가
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl">
                                    <DialogHeader>
                                        <DialogTitle>{editingRole ? '역할 수정' : '역할 추가'}</DialogTitle>
                                        <DialogDescription>
                                            {editingRole ? '기존 역할의 정보를 수정합니다.' : '새로운 역할을 생성하고 서비스 권한을 설정합니다.'}
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="roleId">Role ID</Label>
                                                <Input
                                                    id="roleId"
                                                    value={roleForm.roleId || ''}
                                                    onChange={(e) => setRoleForm({ ...roleForm, roleId: e.target.value })}
                                                    disabled={!!editingRole}
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="roleName">Role Name</Label>
                                                <Input
                                                    id="roleName"
                                                    value={roleForm.roleName || ''}
                                                    onChange={(e) => setRoleForm({ ...roleForm, roleName: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label htmlFor="description">설명</Label>
                                            <Input
                                                id="description"
                                                value={roleForm.description || ''}
                                                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label>서비스별 권한</Label>
                                            <p className="text-xs text-gray-500 mt-1 mb-2">서비스를 선택하면 해당 서비스에 대해 조회·생성·수정·삭제가 모두 허용됩니다. 개별 선택/해제는 불가합니다.</p>
                                            <div className="mt-2 max-h-60 overflow-y-auto border rounded p-2 space-y-1">
                                                {accessData.services.map((service) => {
                                                    const perms = rolePermissions[service.id] || [];
                                                    const hasView = perms.includes('view');
                                                    return (
                                                        <div key={service.id} className="flex items-center gap-2 p-1">
                                                            <Checkbox
                                                                checked={hasView}
                                                                onCheckedChange={(checked) => {
                                                                    if (checked) {
                                                                        setRolePermissions({ ...rolePermissions, [service.id]: ['view', 'create', 'update', 'delete'] });
                                                                    } else {
                                                                        setRolePermissions({ ...rolePermissions, [service.id]: [] });
                                                                    }
                                                                }}
                                                            />
                                                            <span className="text-sm">{service.name || service.id}</span>
                                                            {hasView && <span className="text-xs text-gray-500">(조회·생성·수정·삭제)</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button variant="outline" onClick={() => setRoleModalOpen(false)}>
                                                취소
                                            </Button>
                                            <Button onClick={handleRoleSubmit}>
                                                저장
                                            </Button>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>

                        <Card className="shadow-sm">
                            <div className="p-6">
                                <div className="space-y-3">
                                    {accessData.roles.map((role) => (
                                        <div key={role.roleId} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-200">
                                            <div className="flex items-center gap-4 flex-1">
                                                <div className="p-2.5 bg-purple-50 rounded-lg border border-purple-100">
                                                    <Shield className="h-5 w-5 text-purple-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <h3 className="text-sm font-semibold text-gray-900">{role.roleName}</h3>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mb-1">{role.roleId}</p>
                                                    <p className="text-xs text-gray-600 mb-2">{role.description}</p>
                                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                                        <span>서비스: {role.services.length}개</span>
                                                        <span>•</span>
                                                        <span>멤버: {role.members.length}명</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex gap-1.5 ml-4">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-200"
                                                    onClick={() => handleRoleEdit(role)}
                                                >
                                                    <Edit className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 hover:bg-red-50 hover:border-red-200"
                                                    onClick={() => handleRoleDelete(role.roleId)}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                    {accessData.roles.length === 0 && (
                                        <div className="text-center py-12 text-gray-500">
                                            <Shield className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                                            <p className="text-sm">등록된 역할이 없습니다.</p>
                                            <p className="text-xs text-gray-400 mt-1">새 역할을 추가해보세요.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>
                    </TabsContent>

                    <TabsContent value="groups" className="space-y-4">
                        <div className="mb-4">
                            <h2 className="text-lg font-semibold">그룹(팀) 역할 할당</h2>
                            <p className="text-sm text-gray-500">
                                조직(Organization)에서 정의한 팀 목록에 역할을 할당합니다. 팀에 소속된 사용자는 해당 팀에 부여된 역할을 자동으로 보유합니다. 팀 추가/삭제/이름 변경은 조직 페이지에서만 가능합니다.
                            </p>
                        </div>
                        <Card className="shadow-sm">
                            <div className="p-6">
                                {orgTeams.length === 0 ? (
                                    <div className="p-12 text-center text-gray-500">
                                        <Building className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                                        <p className="text-sm">조직에 등록된 팀이 없습니다. 조직 페이지에서 팀을 먼저 추가해주세요.</p>
                                    </div>
                                ) : (
                                    <>
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>팀 ID</TableHead>
                                                    <TableHead>팀 이름</TableHead>
                                                    <TableHead>상태</TableHead>
                                                    <TableHead>할당된 역할</TableHead>
                                                    <TableHead className="w-[100px]">동작</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {orgTeams.map((team) => {
                                                    const assignedRoleIds = rawData?.roles?.teamRoleAssignments?.[team.teamId] || [];
                                                    return (
                                                        <TableRow key={team.teamId}>
                                                            <TableCell className="font-mono text-sm">{team.teamId}</TableCell>
                                                            <TableCell>{team.teamName}</TableCell>
                                                            <TableCell>{team.status}</TableCell>
                                                            <TableCell>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {assignedRoleIds.length === 0 ? (
                                                                        <span className="text-xs text-gray-400">없음</span>
                                                                    ) : (
                                                                        assignedRoleIds.map((roleId) => (
                                                                            <Badge key={roleId} className="text-xs bg-purple-100 text-purple-700">
                                                                                {accessData.roles.find(r => r.roleId === roleId)?.roleName || roleId}
                                                                            </Badge>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setEditingTeamForRoles(team);
                                                                        setGroupRoleForm(assignedRoleIds.slice());
                                                                        setGroupRoleModalOpen(true);
                                                                    }}
                                                                >
                                                                    역할 할당
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                        <Dialog open={groupRoleModalOpen} onOpenChange={setGroupRoleModalOpen}>
                                            <DialogContent className="max-w-md">
                                                <DialogHeader>
                                                    <DialogTitle>팀 역할 할당</DialogTitle>
                                                    <DialogDescription>
                                                        {editingTeamForRoles && (
                                                            <>팀 &quot;{editingTeamForRoles.teamName}&quot;({editingTeamForRoles.teamId})에 부여할 역할을 선택하세요. 소속 사용자가 이 역할들을 자동으로 보유합니다.</>
                                                        )}
                                                    </DialogDescription>
                                                </DialogHeader>
                                                <div className="space-y-4">
                                                    <div>
                                                        <Label>역할 선택</Label>
                                                        <div className="mt-2 max-h-48 overflow-y-auto border rounded p-2 space-y-2">
                                                            {accessData.roles.length === 0 ? (
                                                                <p className="text-sm text-gray-500">등록된 역할이 없습니다. 역할 관리 탭에서 역할을 먼저 추가해주세요.</p>
                                                            ) : (
                                                                accessData.roles.map((role) => (
                                                                    <label key={role.roleId} className="flex items-center gap-2 p-1 cursor-pointer hover:bg-gray-50 rounded">
                                                                        <Checkbox
                                                                            checked={groupRoleForm.includes(role.roleId)}
                                                                            onCheckedChange={(checked) => {
                                                                                if (checked) {
                                                                                    setGroupRoleForm([...groupRoleForm, role.roleId]);
                                                                                } else {
                                                                                    setGroupRoleForm(groupRoleForm.filter(id => id !== role.roleId));
                                                                                }
                                                                            }}
                                                                        />
                                                                        <span className="text-sm">{role.roleName}</span>
                                                                        <span className="text-xs text-gray-400">({role.roleId})</span>
                                                                    </label>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-end gap-2">
                                                        <Button variant="outline" onClick={() => setGroupRoleModalOpen(false)}>취소</Button>
                                                        <Button onClick={async () => {
                                                            if (!editingTeamForRoles || !rawData || !user?.tenantId) return;
                                                            const updated = { ...(rawData.roles.teamRoleAssignments || {}), [editingTeamForRoles.teamId]: groupRoleForm };
                                                            await dataService.updateTenantAccessControl(user.tenantId, {
                                                                roles: { ...rawData.roles, teamRoleAssignments: updated },
                                                                permissions: rawData.permissions,
                                                                services: rawData.services
                                                            });
                                                            setRawData({ ...rawData, roles: { ...rawData.roles, teamRoleAssignments: updated } });
                                                            setGroupRoleModalOpen(false);
                                                            loadAccessControlData(user.tenantId);
                                                        }}>
                                                            저장
                                                        </Button>
                                                    </div>
                                                </div>
                                            </DialogContent>
                                        </Dialog>
                                    </>
                                )}
                            </div>
                        </Card>
                    </TabsContent>

                    <TabsContent value="services" className="space-y-4">
                        <div>
                            <h2 className="text-lg font-semibold mb-4">서비스 권한 매트릭스</h2>
                            <Card className="shadow-sm">
                                <div className="p-6">
                                    <div className="space-y-4">
                                        {accessData.services.map((service) => {
                                            const perm = rawData?.permissions?.[service.id];
                                            return (
                                            <div key={service.id} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div>
                                                        <h3 className="font-semibold text-gray-900">{service.name || service.id}</h3>
                                                    </div>
                                                </div>
                                                {perm && (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                                        {['view', 'create', 'update', 'delete'].map((action) => {
                                                            const actionPerm = (perm as any)[action];
                                                            if (!actionPerm) return null;
                                                            return (
                                                            <div key={action} className="bg-gray-50 rounded p-3 relative">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <h4 className="text-sm font-medium text-gray-700">
                                                                    {action === 'view' ? '조회' : action === 'create' ? '생성' : action === 'update' ? '수정' : '삭제'}
                                                                </h4>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-6 w-6 p-0"
                                                                        onClick={() => {
                                                                            setEditingPermission({ serviceId: service.id, action });
                                                                            setPermissionForm({
                                                                                roles: actionPerm.roles || [],
                                                                                groups: actionPerm.groups || [],
                                                                                users: actionPerm.users || []
                                                                            });
                                                                            setPermissionModalOpen(true);
                                                                        }}
                                                                    >
                                                                        <Edit className="h-3 w-3" />
                                                                    </Button>
                                                                </div>
                                                                <div className="space-y-2">
                                                                    {actionPerm.groups?.length > 0 && (
                                                                        <div className="flex flex-wrap gap-1">
                                                                            {actionPerm.groups.map((g: string) => (
                                                                                <Badge key={g} className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-100">
                                                                                    그룹: {accessData.roles.find(r => r.roleId === g)?.roleName || g}
                                                                                </Badge>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {actionPerm.roles?.length > 0 && (
                                                                        <div className="flex flex-wrap gap-1">
                                                                            {actionPerm.roles.map((r: string) => {
                                                                                const defined = accessData.roles.find(role => role.roleId === r);
                                                                                return (
                                                                                    <Badge
                                                                                        key={r}
                                                                                        className={defined ? "text-xs bg-purple-100 text-purple-700 hover:bg-purple-100" : "text-xs bg-amber-100 text-amber-800 hover:bg-amber-100"}
                                                                                        title={defined ? undefined : "역할 관리에 정의되지 않은 역할입니다. 권한 편집에서 제거하거나, 역할 관리에 동일 ID로 추가하세요."}
                                                                                    >
                                                                                        역할: {defined ? defined.roleName : `${r} (정의되지 않음)`}
                                                                                    </Badge>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                    {actionPerm.users?.length > 0 && (
                                                                        <div className="flex flex-wrap gap-1">
                                                                            {actionPerm.users.map((u: string) => (
                                                                                <Badge key={u} className="text-xs bg-green-100 text-green-700 hover:bg-green-100">
                                                                                    사용자: {u}
                                                                                </Badge>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {!actionPerm.groups?.length && !actionPerm.roles?.length && !actionPerm.users?.length && (
                                                                        <span className="text-xs text-gray-400">없음</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </Card>
                        </div>

                        {/* 권한 편집 모달 */}
                        <Dialog open={permissionModalOpen} onOpenChange={setPermissionModalOpen}>
                            <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                    <DialogTitle>권한 편집</DialogTitle>
                                    <DialogDescription>
                                        {editingPermission && (
                                            <>
                                                서비스: {accessData.services.find(s => s.id === editingPermission.serviceId)?.name || editingPermission.serviceId}
                                                <br />
                                                액션: {editingPermission.action === 'view' ? '조회' : editingPermission.action === 'create' ? '생성' : editingPermission.action === 'update' ? '수정' : '삭제'}
                                            </>
                                        )}
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                    {/* 역할 선택: 역할 관리에 정의된 역할만 선택 가능. 정의되지 않은 역할은 저장 시 제거됨 */}
                                    <div>
                                        <Label>역할</Label>
                                        <p className="text-xs text-gray-500 mb-1">역할 관리에 정의된 역할만 선택할 수 있습니다. 정의되지 않은 역할이 있으면 저장 시 제거됩니다.</p>
                                        {permissionForm.roles.some(r => !accessData.roles.some(def => def.roleId === r)) && (
                                            <p className="text-xs text-amber-600 mb-2">현재 포함된 정의되지 않은 역할: {permissionForm.roles.filter(r => !accessData.roles.some(def => def.roleId === r)).join(', ')} (저장 시 제거됨)</p>
                                        )}
                                        <div className="mt-2 max-h-40 overflow-y-auto border rounded p-2 space-y-1">
                                            {accessData.roles.map((role) => (
                                                <label key={role.roleId} className="flex items-center gap-2 p-1 cursor-pointer hover:bg-gray-50 rounded">
                                                    <Checkbox
                                                        checked={permissionForm.roles.includes(role.roleId)}
                                                        onCheckedChange={(checked) => {
                                                            if (checked) {
                                                                setPermissionForm({
                                                                    ...permissionForm,
                                                                    roles: [...permissionForm.roles, role.roleId]
                                                                });
                                                            } else {
                                                                setPermissionForm({
                                                                    ...permissionForm,
                                                                    roles: permissionForm.roles.filter(r => r !== role.roleId)
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <span className="text-sm">{role.roleName}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* 그룹(팀) 선택 - 조직 팀 연동 시 사용 예정 */}
                                    <div>
                                        <Label>그룹</Label>
                                        <div className="mt-2 max-h-40 overflow-y-auto border rounded p-2 space-y-1 text-sm text-gray-500">
                                            그룹(팀) 할당은 조직 팀 연동 후 제공됩니다.
                                        </div>
                                    </div>

                                    {/* 사용자 입력 */}
                                    <div>
                                        <Label>사용자 ID (쉼표로 구분)</Label>
                                        <Input
                                            placeholder="user1, user2, user3"
                                            value={permissionForm.users.join(', ')}
                                            onChange={(e) => {
                                                const users = e.target.value.split(',').map(u => u.trim()).filter(u => u);
                                                setPermissionForm({ ...permissionForm, users });
                                            }}
                                        />
                                    </div>

                                    <div className="flex justify-end gap-2">
                                        <Button variant="outline" onClick={() => setPermissionModalOpen(false)}>
                                            취소
                                        </Button>
                                        <Button onClick={async () => {
                                            if (!editingPermission || !user?.tenantId) return;
                                            
                                            try {
                                                setLoading(true);
                                                
                                                // rawData의 permissions 복사
                                                const updatedPermissions = { ...rawData?.permissions };
                                                
                                                // 해당 서비스의 권한 구조 확인
                                                if (!updatedPermissions[editingPermission.serviceId]) {
                                                    updatedPermissions[editingPermission.serviceId] = {
                                                        name: accessData.services.find(s => s.id === editingPermission.serviceId)?.name || editingPermission.serviceId
                                                    };
                                                }
                                                
                                                // 정의된 역할만 유지 (정의되지 않은 역할은 저장 시 제거)
                                                const definedRoleIds = (permissionForm.roles || []).filter(r => accessData.roles.some(def => def.roleId === r));
                                                updatedPermissions[editingPermission.serviceId][editingPermission.action] = {
                                                    roles: definedRoleIds,
                                                    groups: permissionForm.groups,
                                                    users: permissionForm.users
                                                };
                                                
                                                // 백엔드에 저장
                                                await dataService.updateTenantAccessControl(user.tenantId, {
                                                    ...rawData,
                                                    permissions: updatedPermissions
                                                });
                                                
                                                // UI 갱신
                                                await loadAccessControlData(user.tenantId);
                                                setPermissionModalOpen(false);
                                            } catch (error) {
                                                console.error('권한 업데이트 실패:', error);
                                                alert('권한 업데이트에 실패했습니다.');
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}>
                                            저장
                                        </Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </TabsContent>

                    <TabsContent value="user-overrides" className="space-y-4">
                        <div>
                            <h2 className="text-lg font-semibold">사용자 추가 권한</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                역할·팀으로 부여된 권한 외에, 특정 사용자에게만 서비스 접근을 허용할 수 있습니다. 서비스 선택 시 조회·생성·수정·삭제가 모두 허용됩니다.
                            </p>
                        </div>
                        <div className="flex justify-end">
                            <Dialog open={userOverrideModalOpen} onOpenChange={setUserOverrideModalOpen}>
                                <DialogTrigger asChild>
                                    <Button onClick={handleUserOverrideCreate} className="flex items-center gap-2">
                                        <Plus className="h-4 w-4" />
                                        추가 권한 설정
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl">
                                    <DialogHeader>
                                        <DialogTitle>{editingUserOverride ? '추가 권한 수정' : '추가 권한 설정'}</DialogTitle>
                                        <DialogDescription>
                                            {editingUserOverride ? '사용자의 추가 권한을 수정합니다.' : '특정 사용자에게 추가 서비스 권한을 부여합니다.'}
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                        <div>
                                            <Label>사용자 선택</Label>
                                            <p className="text-xs text-gray-500 mb-2">조직(Organization)에 등록된 사용자 중 추가 권한을 부여할 사용자를 선택하세요.</p>
                                            <Select
                                                value={userOverrideForm.userId || ''}
                                                onValueChange={(value) => {
                                                    const u = orgUsers.find(ou => ou.userId === value);
                                                    setUserOverrideForm({ ...userOverrideForm, userId: value, name: u?.displayName || value });
                                                    // 선택한 사용자의 현재 "추가 권한"(permissionMatrix.users) 로드
                                                    const perms: Record<string, string[]> = {};
                                                    if (rawData?.permissions && value) {
                                                        for (const [serviceId, sp] of Object.entries(rawData.permissions)) {
                                                            if (serviceId === 'schemaVersion') continue;
                                                            const servicePerm = sp as any;
                                                            let hasAny = false;
                                                            for (const action of ['view', 'create', 'update', 'delete']) {
                                                                if (servicePerm[action]?.users?.includes(value)) {
                                                                    hasAny = true;
                                                                    break;
                                                                }
                                                            }
                                                            if (hasAny) perms[serviceId] = ['view', 'create', 'update', 'delete'];
                                                        }
                                                    }
                                                    setUserPermissions(perms);
                                                }}
                                                disabled={!!editingUserOverride}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="사용자 선택" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {orgUsers.map((u) => (
                                                        <SelectItem key={u.userId} value={u.userId}>
                                                            {u.displayName} ({u.userId})
                                                        </SelectItem>
                                                    ))}
                                                    {orgUsers.length === 0 && (
                                                        <div className="py-2 px-2 text-sm text-gray-500">조직에 등록된 사용자가 없습니다.</div>
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>서비스 접근 권한</Label>
                                            {userOverrideForm.userId ? (
                                                <>
                                                    <p className="text-xs text-gray-500 mt-1 mb-2">아래에서 이미 접근 가능한 서비스(역할/팀)를 확인한 뒤, <strong>추가로 부여할 서비스</strong>만 선택하세요. 서비스 선택 시 조회·생성·수정·삭제가 모두 허용됩니다.</p>
                                                    {(() => {
                                                        const alreadyHasFromRole = getServiceIdsFromRoleForUser(userOverrideForm.userId);
                                                        return (
                                                            <div className="mt-2 max-h-60 overflow-y-auto border rounded p-2 space-y-1">
                                                                {accessData.services.map((service) => {
                                                                    const perms = userPermissions[service.id] || [];
                                                                    const hasOverride = perms.includes('view');
                                                                    const hasFromRole = alreadyHasFromRole.includes(service.id);
                                                                    return (
                                                                        <div key={service.id} className="flex items-center gap-2 p-1">
                                                                            <Checkbox
                                                                                checked={hasFromRole || hasOverride}
                                                                                disabled={hasFromRole}
                                                                                onCheckedChange={(checked) => {
                                                                                    if (hasFromRole) return;
                                                                                    if (checked) {
                                                                                        setUserPermissions({ ...userPermissions, [service.id]: ['view', 'create', 'update', 'delete'] });
                                                                                    } else {
                                                                                        setUserPermissions({ ...userPermissions, [service.id]: [] });
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <span className="text-sm">{service.name || service.id}</span>
                                                                            {hasFromRole && <span className="text-xs text-gray-500">(역할/팀으로 보유)</span>}
                                                                            {!hasFromRole && hasOverride && <span className="text-xs text-blue-600">(추가 권한)</span>}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })()}
                                                </>
                                            ) : (
                                                <p className="text-sm text-gray-500 mt-2">사용자를 먼저 선택하면 해당 사용자가 이미 접근 가능한 서비스와 추가로 부여할 서비스를 설정할 수 있습니다.</p>
                                            )}
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button variant="outline" onClick={() => setUserOverrideModalOpen(false)}>
                                                취소
                                            </Button>
                                            <Button onClick={handleUserOverrideSubmit}>
                                                저장
                                            </Button>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>

                        <Card className="shadow-sm">
                            <div className="p-6">
                                {orgUsers.length === 0 ? (
                                    <div className="text-center py-12 text-gray-500">
                                        <Key className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                                        <p className="text-sm">조직에 등록된 사용자가 없습니다.</p>
                                        <p className="text-xs text-gray-400 mt-1">조직 페이지에서 사용자를 먼저 추가해주세요.</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>사용자</TableHead>
                                                <TableHead>소속 팀</TableHead>
                                                <TableHead>부여된 역할</TableHead>
                                                <TableHead>추가 서비스 권한</TableHead>
                                                <TableHead className="w-[100px]">동작</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {orgUsers.map((orgUser) => {
                                                const userOverride = accessData.userOverrides.find(o => o.userId === orgUser.userId);
                                                const additionalServices = userOverride?.additional_services ?? [];
                                                return (
                                                    <TableRow key={orgUser.userId}>
                                                        <TableCell>
                                                            <div>
                                                                <span className="font-medium text-sm">{orgUser.displayName}</span>
                                                                <p className="text-xs text-gray-500 font-mono">{orgUser.userId}</p>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-wrap gap-1">
                                                                {getTeamNamesForUser(orgUser.userId).length === 0 ? (
                                                                    <span className="text-xs text-gray-400">—</span>
                                                                ) : (
                                                                    getTeamNamesForUser(orgUser.userId).map((name) => (
                                                                        <Badge key={name} variant="outline" className="text-xs">
                                                                            {name}
                                                                        </Badge>
                                                                    ))
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-wrap gap-1">
                                                                {getRoleNamesForUser(orgUser.userId).length === 0 ? (
                                                                    <span className="text-xs text-gray-400">—</span>
                                                                ) : (
                                                                    getRoleNamesForUser(orgUser.userId).map((name) => (
                                                                        <Badge key={name} variant="outline" className="text-xs bg-purple-50">
                                                                            {name}
                                                                        </Badge>
                                                                    ))
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-wrap gap-1">
                                                                {additionalServices.length === 0 ? (
                                                                    <span className="text-xs text-gray-400">없음</span>
                                                                ) : (
                                                                    additionalServices.map((serviceId) => (
                                                                        <Badge key={serviceId} variant="secondary" className="text-xs">
                                                                            {getServiceName(serviceId)}
                                                                        </Badge>
                                                                    ))
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex gap-1">
                                                                <Button variant="outline" size="sm" onClick={() => openUserOverrideDialogForUser(orgUser.userId)} title="추가 권한 편집">
                                                                    <Edit className="h-3.5 w-3.5" />
                                                                </Button>
                                                                {userOverride && (
                                                                    <Button variant="outline" size="sm" className="hover:bg-red-50" onClick={() => handleUserOverrideDelete(orgUser.userId)} title="추가 권한 제거">
                                                                        <Trash2 className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                )}
                            </div>
                        </Card>
                    </TabsContent>
                </Tabs>
            )}
        </div>
    );
};

AccessControlPage.displayName = 'AccessControlPage';
